import { Component, inject, Inject, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { Coupon, CouponCreatePayload } from "@models/coupon";
import { CouponsService } from "@services/coupons.service";
import { EventsManagementService } from "@services/events-management.service";
import { SnackBarService } from "@services/snack-bar.service";
import { DateTime } from "luxon";
import { DatePickerModule } from "primeng/datepicker";
import { MultiSelectModule } from "primeng/multiselect";
import { catchError, EMPTY, finalize, forkJoin, take } from "rxjs";
import { sortBatchesByStartDate } from "../../../../utils/utils";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { DialogHeaderComponent } from "../../../../shared/components/dialog-header/dialog-header.component";
import { LoadingComponent } from "../../../../shared/components/loading/loading.component";

export interface CouponFormData {
  eventId: number;
  coupon?: Coupon;
}

interface SelectOption {
  label: string;
  value: number;
}

@Component({
  selector: "app-coupon-form-modal",
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    DatePickerModule,
    MultiSelectModule,
    ButtonComponent,
    DialogHeaderComponent,
    LoadingComponent,
  ],
  templateUrl: "./coupon-form-modal.component.html",
  styleUrl: "./coupon-form-modal.component.scss",
})
export class CouponFormModalComponent implements OnInit {
  private readonly _fb = inject(FormBuilder);
  private readonly _couponsService = inject(CouponsService);
  private readonly _eventsService = inject(EventsManagementService);
  private readonly _snackbar = inject(SnackBarService);
  readonly dialogRef = inject(MatDialogRef<CouponFormModalComponent, Coupon>);

  readonly coupon = this._data.coupon ?? null;
  readonly isEdit = !!this.coupon;
  // Cupom já utilizado: só description / quantity / expires_at podem mudar.
  readonly hasUsage = (this.coupon?.used_count ?? 0) > 0;

  loading = false;
  loadingTickets = false;
  ticketOptions: SelectOption[] = [];
  maxExpiresAt: Date | null = null;
  minExpiresAt = DateTime.now().toJSDate();

  form = this._fb.group({
    event_id: this._fb.control<number | null>(
      this._data.eventId,
      Validators.required,
    ),
    code: this._fb.control("", {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.maxLength(30),
        Validators.pattern(/^[A-Za-z0-9]+$/),
      ],
    }),
    description: this._fb.control("", { nonNullable: true }),
    discount_percentage: this._fb.control<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    quantity: this._fb.control<number | null>(null, [
      Validators.required,
      Validators.min(1),
    ]),
    // Limite de unidades descontadas por uso (inteiro > 0, default 1).
    max_discounted_items: this._fb.control<number | null>(1, [
      Validators.required,
      Validators.min(1),
      this._integer,
    ]),
    expires_at: this._fb.control<Date | null>(null, Validators.required),
    ticket_ids: this._fb.control<number[]>([], {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(1)],
    }),
  });

  controls = this.form.controls;

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly _data: CouponFormData,
  ) {}

  ngOnInit() {
    if (this.coupon) {
      this._initEditMode(this.coupon);
    } else {
      this._loadEventContext(this._data.eventId);
    }
  }

  confirm() {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const raw = this.form.getRawValue();
    const eventId = raw.event_id!;
    const payload: CouponCreatePayload = {
      code: raw.code.toUpperCase(),
      description: raw.description?.trim() || undefined,
      discount_percentage: raw.discount_percentage!,
      quantity: raw.quantity!,
      max_discounted_items: raw.max_discounted_items ?? 1,
      expires_at: DateTime.fromJSDate(raw.expires_at!).toISO()!,
      ticket_ids: raw.ticket_ids ?? [],
    };

    const request$ = this.coupon
      ? this._couponsService.update(eventId, this.coupon.id, payload)
      : this._couponsService.create(eventId, payload);

    this.loading = true;
    request$
      .pipe(
        take(1),
        catchError((err) => {
          this._snackbar.openSnackBar(
            err?.error?.error ?? "Não foi possível salvar o cupom.",
            "Fechar",
            "error",
          );
          return EMPTY;
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe((coupon) => {
        this._snackbar.openSnackBar(
          this.isEdit ? "Cupom atualizado." : "Cupom criado.",
          "Fechar",
          "success",
        );
        this.dialogRef.close(coupon);
      });
  }

  private _initEditMode(coupon: Coupon) {
    this.form.patchValue({
      event_id: coupon.event_id,
      code: coupon.code,
      description: coupon.description ?? "",
      discount_percentage: coupon.discount_percentage,
      quantity: coupon.quantity,
      max_discounted_items: coupon.max_discounted_items ?? 1,
      expires_at: DateTime.fromISO(coupon.expires_at).toJSDate(),
    });

    // Evento não muda na edição.
    this.controls.event_id.disable();

    if (this.hasUsage) {
      // API ignora code / discount_percentage / max_discounted_items /
      // ticket_ids quando há uso (só description / quantity / expires_at).
      this.controls.code.disable();
      this.controls.discount_percentage.disable();
      this.controls.max_discounted_items.disable();
      this.controls.ticket_ids.disable();
      // quantity não pode ficar abaixo do já utilizado.
      this.controls.quantity.setValidators([
        Validators.required,
        Validators.min(coupon.used_count),
      ]);
      this.controls.quantity.updateValueAndValidity();
    }

    this._loadEventContext(coupon.event_id);
  }

  // Opções do seletor de evento (modo criação). Organizador: o backend já
  // filtra por data (status active). Colaborador: o filtro de papel manager
  // (doc §2) e de data vivem no próprio stream do my_events_collab, que não
  // aceita esses parâmetros. O map final só faz o merge + dedup.
  private _loadEventContext(eventId: number) {
    this.loadingTickets = true;
    forkJoin({
      info: this._eventsService.getEventInfo(eventId),
      batches: this._eventsService.getEventBatches(eventId),
    })
      .pipe(
        catchError(() => {
          this._snackbar.openSnackBar(
            "Não foi possível carregar as categorias do evento.",
            "Fechar",
            "error",
          );
          return EMPTY;
        }),
        finalize(() => (this.loadingTickets = false)),
      )
      .subscribe(({ info, batches }) => {
        this.maxExpiresAt = info.end_date
          ? DateTime.fromISO(info.end_date).toJSDate()
          : null;
        // Na criação, pré-preenche a validade com o fim do evento (o usuário
        // pode ajustar). Na edição, mantém o expires_at já carregado do cupom.
        if (!this.coupon && this.maxExpiresAt) {
          this.controls.expires_at.setValue(this.maxExpiresAt);
        }
        // Numera os lotes por ordem de início (padrão "Lote N" do app) e
        // qualifica cada categoria com o lote a que pertence.
        this.ticketOptions = sortBatchesByStartDate(batches).flatMap(
          (batch, index) =>
            batch.tickets
              .filter((ticket) => ticket.id != null)
              .map((ticket) => ({
                label: `${ticket.title} — Lote ${index + 1}`,
                value: ticket.id!,
              })),
        );
        if (this.coupon?.ticket_ids?.length) {
          this.controls.ticket_ids.setValue(this.coupon.ticket_ids);
        }
      });
  }

  private _integer(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined || value === "") {
      return null;
    }
    return Number.isInteger(Number(value)) ? null : { integer: true };
  }
}
